import {
  getActivePendingToolConfirmation,
  type ActivePendingToolConfirmation,
} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {EstablishedAhpPrompt, EstablishedAhpPromptCommandType} from '@github-ui/ahp-session-client/contracts'
import type {ChatState, PendingMessage} from '@microsoft/agent-host-protocol'
import {useCallback, useSyncExternalStore, type ReactNode, type RefObject} from 'react'

import {EstablishedAhpSessionConfirmation} from './EstablishedAhpSessionConfirmation'
import {EstablishedAhpSessionPlan} from './EstablishedAhpSessionPlan'
import {EstablishedAhpSessionPrompt} from './EstablishedAhpSessionPrompt'
import {QuickChatQueuedMessages} from './QuickChatQueuedMessages'
import {useEstablishedAhpComposerFocusReturn} from '../hooks/use-established-ahp-composer-focus-return'
import {useOptionalEstablishedAhpSession} from '../hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '../hooks/use-established-ahp-session-view'

export interface EstablishedAhpSessionSurfaceProps {
  state?: ChatState
  queuedMessages?: readonly PendingMessage[]
  suppressQueuedMessages?: boolean
  queueReadOnly?: boolean
  readOnly?: boolean
  takeoverEnabled?: boolean
  activeConfirmation?: ActivePendingToolConfirmation
  pendingPrompt?: EstablishedAhpPrompt
  onPromptRespond?: (
    toolCallId: string,
    response: unknown,
    commandType: EstablishedAhpPromptCommandType,
  ) => boolean | void
  isSubmittingPrompt?: boolean
  confirmationClassName?: string
  promptClassName?: string
  confirmationTestId?: string
  promptTestId?: string
  focusTargetRef?: RefObject<HTMLElement | null>
  onFocusFallback?: () => void
  onExpandedChange?: (expanded: boolean) => void
  onRemoveStarted?: (id: string) => void
  onSendNowStarted?: (message: PendingMessage) => void
  renderQueuedMessage?: (message: PendingMessage) => ReactNode
  queuedMessagesAriaLabel?: string
  fillAvailableSpace?: boolean
  children?: ReactNode
}

/**
 * Established AHP surfaces share queue, plan, takeover, and focus behavior.
 * Products provide only their normal composer as children.
 */
export function EstablishedAhpSessionSurface({
  state,
  queuedMessages,
  suppressQueuedMessages = false,
  queueReadOnly = false,
  readOnly = false,
  takeoverEnabled = true,
  activeConfirmation,
  pendingPrompt,
  onPromptRespond,
  isSubmittingPrompt = false,
  confirmationClassName,
  promptClassName,
  confirmationTestId = 'established-ahp-session-confirmation',
  promptTestId = 'established-ahp-session-prompt',
  focusTargetRef,
  onFocusFallback,
  onExpandedChange,
  onRemoveStarted,
  onSendNowStarted,
  renderQueuedMessage,
  queuedMessagesAriaLabel = 'Queued messages',
  fillAvailableSpace = false,
  children,
}: EstablishedAhpSessionSurfaceProps) {
  const session = useOptionalEstablishedAhpSession()
  const view = useOptionalEstablishedAhpSessionViewState()
  const canonicalState = session ? view.state : state
  const resolvedQueuedMessages = session ? (queuedMessages ?? canonicalState?.queuedMessages) : undefined
  const derivedConfirmation =
    canonicalState?.activeTurn && !Array.isArray(canonicalState.activeTurn.responseParts)
      ? undefined
      : canonicalState
        ? getActivePendingToolConfirmation(canonicalState)
        : undefined
  const resolvedConfirmation = takeoverEnabled ? (activeConfirmation ?? derivedConfirmation) : undefined
  const resolvedPrompt = takeoverEnabled ? pendingPrompt : undefined
  const takeoverKey = resolvedConfirmation
    ? `${resolvedConfirmation.turnId}:${resolvedConfirmation.toolCallId}`
    : resolvedPrompt
      ? `ask_user:${resolvedPrompt.toolCallId}`
      : undefined
  const subscribeToComposerFocus = useCallback(
    (onStoreChange: () => void) => {
      if (takeoverKey) return () => undefined
      const target = focusTargetRef?.current
      if (!target) return () => undefined
      target.addEventListener('focus', onStoreChange)
      target.addEventListener('blur', onStoreChange)
      return () => {
        target.removeEventListener('focus', onStoreChange)
        target.removeEventListener('blur', onStoreChange)
      }
    },
    [focusTargetRef, takeoverKey],
  )
  const getComposerFocusSnapshot = useCallback(
    () => document.activeElement === focusTargetRef?.current,
    [focusTargetRef],
  )
  const composerHadFocus = useSyncExternalStore(subscribeToComposerFocus, getComposerFocusSnapshot, () => false)
  const focusComposerControl = useCallback(() => {
    const target = focusTargetRef?.current
    if (target?.isConnected && !target.hasAttribute('disabled')) {
      target.focus()
      if (document.activeElement === target) return
    }

    onFocusFallback?.()
  }, [focusTargetRef, onFocusFallback])

  const {onFocusCapture, onBlurCapture} = useEstablishedAhpComposerFocusReturn(takeoverKey, focusComposerControl)

  const child = resolvedConfirmation ? (
    <EstablishedAhpSessionConfirmation
      key={takeoverKey}
      confirmation={resolvedConfirmation}
      className={confirmationClassName}
      data-testid={confirmationTestId}
      onBlurCapture={onBlurCapture}
      onFocusCapture={onFocusCapture}
      readOnly={readOnly || !session}
    />
  ) : resolvedPrompt && onPromptRespond ? (
    <div
      key={takeoverKey}
      className={promptClassName}
      data-testid={promptTestId}
      onBlurCapture={onBlurCapture}
      onFocusCapture={onFocusCapture}
    >
      <EstablishedAhpSessionPrompt
        prompt={resolvedPrompt}
        onRespond={onPromptRespond}
        disabled={readOnly || !session || isSubmittingPrompt}
        autoFocus={composerHadFocus}
      />
    </div>
  ) : (
    children
  )

  return (
    <QuickChatQueuedMessages
      queuedMessages={suppressQueuedMessages ? [] : resolvedQueuedMessages}
      readOnly={queueReadOnly || readOnly || !session}
      onExpandedChange={onExpandedChange}
      onRemoveStarted={onRemoveStarted}
      onSendNowStarted={onSendNowStarted}
      renderQueuedMessage={renderQueuedMessage}
      queuedMessagesAriaLabel={queuedMessagesAriaLabel}
      onFocusFallback={focusComposerControl}
      fillAvailableSpace={fillAvailableSpace}
    >
      {session?.planning ? <EstablishedAhpSessionPlan /> : null}
      {child}
    </QuickChatQueuedMessages>
  )
}

EstablishedAhpSessionSurface.displayName = 'EstablishedAhpSessionSurface'
